import { compileStyles } from 'lib/styled-helpers'
import React, { FC, InputHTMLAttributes, useRef, useState } from 'react'
import styled, { useTheme } from 'styled-components'
import { Icon } from 'ui/icon'

import { getProjectName } from '../../lib/get-project-name'

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'size'> {
  value: string
  block?: boolean
  type?: string
  label: string | null
  error?: string
  formatter?: (value: string) => string
  parser?: (value: string) => string
  onEnter?: () => void
  size?: 'medium' | 'small'
  onChange?: (value: string) => void
}

export const Input: FC<InputProps> = ({
  type = 'text',
  label,
  value,
  onChange,
  error,
  parser,
  formatter,
  onFocus,
  onBlur,
  onEnter,
  size = 'medium',
  block = false,
  required,
  ...restProps
}) => {
  const theme = useTheme()

  const [isFocused, setIsFocused] = useState(false)

  const inputRef = useRef<HTMLInputElement>(null)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parser ? parser(e.target.value) : e.target.value
    onChange && onChange(value)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && onEnter) onEnter()
  }

  return (
    <Wrapper block={block} onClick={() => inputRef?.current?.focus()} size={size}>
      <StyledInput
        type={type}
        value={formatter ? formatter(value) : value}
        ref={inputRef}
        onFocus={(e) => {
          setIsFocused(true)
          onFocus && onFocus(e)
        }}
        onBlur={(e) => {
          setIsFocused(false)
          onBlur && onBlur(e)
        }}
        onKeyDown={handleKeyDown}
        onChange={handleChange}
        label={label}
        {...restProps}
      />
      {label !== null && (
        <Label movedOut={isFocused || value.length > 0} error={error}>
          {label}
          {required && '*'}
        </Label>
      )}
      {getProjectName() !== 'avito' && (
        <BottomBorder
          value={value}
          visible={isFocused}
          error={error}
          movedOut={isFocused || value.length > 0}
          isFocused={isFocused}
        />
      )}

      {error && !isFocused && (
        <>
          <ErrorMessage>{error}</ErrorMessage>
          <IconError>
            <Icon name="info-bmw" color={theme.palette.alert} />
          </IconError>
        </>
      )}
    </Wrapper>
  )
}

const Wrapper = styled.div<{ block: boolean; size: 'medium' | 'small' }>`
  position: relative;
  width: ${({ block }) => (block ? '100%' : '29.2rem')};
  height: ${({ size }) => {
    if (getProjectName() === 'avito') return '6.4rem'
    return 'auto'
  }};
`

const Label = styled.label<{ movedOut: boolean; error?: string }>`
  position: absolute;
  bottom: ${({ movedOut, error, theme }) => {
    return error && !movedOut ? '10px' : 0
  }};
  left: 0;
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
  padding-left: ${({ theme }) => theme.spacing[5]};
  pointer-events: none;
  box-sizing: border-box;
  transition: transform 0.25s, font-size 0.25s;
  color: ${({ theme }) => theme.palette.grey[500]};
  font-weight: 300;
  ${({ theme }) => (getProjectName() === 'avito' ? '' : theme.textStyles.h6)};
  transform: ${({ movedOut, theme }) => movedOut && `translateY(-${theme.spacing[4]})`};
  ${({ movedOut, theme }) => movedOut && theme.textStyles.p3};
`

const BottomBorder = styled.div<{
  value: string | number
  visible: boolean
  error?: string
  movedOut: boolean
  isFocused: boolean
}>`
  position: absolute;
  bottom: ${({ movedOut, error, isFocused, theme }) => {
    if (isFocused) return 0
    return error && !movedOut ? '20px' : 0
  }};
  width: 100%;
  height: 3px;
  background: ${({ theme, error }) => (error ? theme.palette.alert : theme.palette.brand.primary)};
  transform: scaleY(${({ visible }) => (visible ? 1 : 0)});
  transform-origin: 50% 100% 0;
  transition: transform 0.25s;
`

const StyledInput = styled.input<{ label: null | string }>`
  position: relative;
  width: 100%;
  height: 100%;
  padding: ${({ label }) => (label === null ? '0.6rem 0.8rem' : '2.8rem 1.55rem 0.9rem')};
  padding: ${() => getProjectName() === 'avito' && '0.9rem 1.55rem 0.9rem'};
  outline: none;
  box-sizing: border-box;
  margin-bottom: ${() => getProjectName() === 'avito' && '-5px'};

  &:hover ~ ${BottomBorder} {
    transform: scaleY(1);
  }

  ${({ theme }) =>
    getProjectName() === 'avito' &&
    'font-size: 1.5rem !important; font-family: "Manrope",-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto" ,"Helvetica Neue", Arial, sans-serif,  "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol" !important'};

  ${({ theme }) => `
    border: 1px solid ${theme.palette.grey[200]}};
    color: ${theme.palette.grey[900]};
    ${compileStyles(theme.textStyles.h6)};
  `}

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  font-weight: 300;
  border-radius: ${() => (getProjectName() === 'avito' ? '16px' : 0)};
  font-size: ${() => getProjectName() === 'bmw' && '1.5rem'};
`

const ErrorMessage = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.palette.alert};
  padding: 4px 0;
  ${({ theme }) => theme.textStyles.p3};
`

const IconError = styled.i`
  position: absolute;
  top: -11px;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({ theme }) => theme.spacing[16]};
  height: 100%;
  cursor: pointer;
`
